import React from "react";
import { Box, Typography, CardContent, useTheme } from "@mui/material";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { StyledCard } from "../../common/StyledCard";
import { formatCurrency } from "../../../utils/currencyUtils";
import { BranchPerformanceBiDto } from "../../../shared/dtos/bi/BranchPerformanceBiDto";

interface TopBranchesWidgetProps {
  data?: BranchPerformanceBiDto[];
}

const TopBranchesWidget: React.FC<TopBranchesWidgetProps> = ({ data = [] }) => {
  const theme = useTheme();

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
          Top Performing Branches
        </Typography>

        {data.length > 0 ? (
          <Box sx={{ flexGrow: 1, width: "100%", minHeight: 350 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data}
                margin={{ top: 20, right: 20, bottom: 20, left: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={theme.palette.neutral.dark}
                />
                <XAxis
                  dataKey="branchName"
                  tick={{ fontSize: 11, fill: theme.palette.neutral.light }}
                  tickMargin={10}
                  tickFormatter={(val) =>
                    val.length > 15 ? `${val.substring(0, 15)}...` : val
                  }
                />
                <YAxis
                  yAxisId="left"
                  tickFormatter={(val) =>
                    new Intl.NumberFormat("en-US", {
                      notation: "compact",
                    }).format(val)
                  }
                  tick={{ fontSize: 11, fill: theme.palette.neutral.light }}
                  tickMargin={10}
                />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.05)" }}
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 8,
                    borderColor: theme.palette.neutral.dark,
                    color: theme.palette.neutral.light,
                  }}
                  formatter={(value, name, props) => [
                    formatCurrency(Number(value)),
                    name === "totalRevenue"
                      ? `Revenue (${props.payload.transactionCount} txns)`
                      : "Profit",
                  ]}
                />
                <Legend
                  wrapperStyle={{
                    paddingTop: "20px",
                    color: theme.palette.neutral.light,
                  }}
                />
                <Bar
                  yAxisId="left"
                  dataKey="totalRevenue"
                  name="Revenue"
                  fill={theme.palette.info.main}
                  barSize={40}
                  radius={[4, 4, 0, 0]}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="totalProfit"
                  name="Profit"
                  stroke="#00e676"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              minHeight: 350,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              No branch data available.
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default TopBranchesWidget;
